import { Button, Divider, Grid, makeStyles } from '@material-ui/core'
import { useObserver } from 'mobx-react-lite'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CentricTypography as Typography } from '@core/component/CentricTypography'
import { Clear2Icon, ClearIcon } from '@core/icons'
import { DraggableSource } from '@components/Utils/DraggableSource'
import { DropTarget } from '@components/Utils/DropTarget'
import AddIcon from '@material-ui/icons/Add'
import { CNFGroupByRow } from './components/CNFGroupByRow'
import classNames from 'classnames'
import { GROUP_UPDATE_HANDLERES } from './constants/CNFGroupByConstants'

const useStyles = makeStyles(theme => ({
  groupBycontainer: {
    height: '100%',
    overflow: 'hidden'
  },
  popupHeader: {
    height: 50
  },
  title: {
    padding: '25px 25px 5px 25px',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  },
  groupByBody: {
    width: '100%',
    height: 'calc(100% - 120px)',
    overflowY: 'auto',
    padding: '15px 20px 10px 20px'
  },
  groupByFooter: {
    height: 60,
    padding: '15px 20px 10px 20px'
  },
  dragWrapperRow: {
    width: '100%'
  },
  draggableSource: {
    '&> div': {
      width: '100%',
      display: 'flex',
      flexWrap: 'wrap',
      boxSizing: 'border-box'
    }
  },
  hiddenBtn: {
    display: 'none',
    width: 0,
    height: 0
  },
  infoMessage: {
    justifyContent: 'center',
    margin: '40px 0px',
    '& h6': {
      textTransform: 'initial',
      textAlign: 'center'
    }
  },
  groupDivider: {
    width: '100%',
    margin: '16px 0px'
  },
  clearIcon: {
    cursor: 'pointer'
  }
}))

export const CNFGroupByContainer = ({
  cnfGroupByUIStore,
  applyGroupByOptions,
  onClose
}) => {
  return useObserver(function useHook() {
    const classes = useStyles()
    const { t } = useTranslation()

    const {
      groupByAttributes,
      addedGroupByAttributes,
      addNewGroup,
      maxGroupByAttributeRows
    } = cnfGroupByUIStore
    const hasEmptyRow = addedGroupByAttributes.some(item => !item?.id)
    const disableAddBtn =
      hasEmptyRow ||
      addedGroupByAttributes.length >= maxGroupByAttributeRows ||
      !groupByAttributes.length

    const addedGroupsData = useMemo(() => {
      return addedGroupByAttributes.map((item, index, data) => {
        const canDragDrop = item.id
        return (
          <Grid container>
            <DropTarget
              key={item.id}
              className={classNames(classes.dragWrapperRow, classes.draggableSource)}
              canDrop={canDragDrop}
              handleDrop={draggedItem => {
                applyGroupByOptions(GROUP_UPDATE_HANDLERES.DRAG, { draggedItem, index })
              }}
            >
              <DraggableSource
                libraryItem={{
                  id: item.id,
                  index
                }}
                canDrag={canDragDrop}
                className={classNames(classes.dragWrapperRow)}
              >
                <CNFGroupByRow
                  rowData={item}
                  groupByAttributes={groupByAttributes}
                  index={index}
                  addedGroupByAttributes={addedGroupByAttributes}
                  applyGroupByOptions={applyGroupByOptions}
                />
              </DraggableSource>
            </DropTarget>
            {data[index + 1] ? (
              <Divider orientation='horizontal' className={classes.groupDivider} />
            ) : null}
          </Grid>
        )
      })
    }, [
      classes.dragWrapperRow,
      classes.draggableSource,
      classes.groupDivider,
      groupByAttributes,
      addedGroupByAttributes
    ])

    return (
      <Grid container direction='row' className={classes.groupBycontainer}>
        <Grid container className={classes.popupHeader}>
          <Grid className={classes.title}>
            <Typography variant='h4'>{t('common.label.groupBy', 'Group By')}</Typography>
            <div className={classes.clearIcon} onClick={onClose}>
              <ClearIcon />
            </div>
          </Grid>
        </Grid>

        <Grid className={classes.groupByBody}>
          {!addedGroupByAttributes.length && groupByAttributes.length ? (
            <Grid container className={classes.infoMessage}>
              <Typography variant={'subtitle1Grey'}>
                {t(
                  'bb.settings.productCardConfigSettings.labels.defaultGroupNote',
                  'Default grouping is applied. To add custom grouping please add groups'
                )}
              </Typography>
            </Grid>
          ) : null}

          {!groupByAttributes.length ? (
            <Grid container className={classes.infoMessage}>
              <Typography variant={'subtitle1Grey'}>
                {t(
                  'bb.settings.productCardConfigSettings.labels.noGroupAttributeNote',
                  'No any attribute is configured for custom grouping, Please connect with admin to configure attributes for grouping'
                )}
              </Typography>
            </Grid>
          ) : null}
          {addedGroupsData}
        </Grid>

        <Grid container className={classes.groupByFooter}>
          <Button className={classes.hiddenBtn} />
          <Button
            disabled={disableAddBtn}
            onClick={() => {
              addNewGroup(hasEmptyRow)
            }}
          >
            <AddIcon />
            <Typography variant={disableAddBtn ? 'subtitle1Grey' : 'subtitle1'}>
              {t('bb.settings.productCardConfigSettings.labels.addGroup', 'Add Group')}
            </Typography>
          </Button>

          <Button
            disabled={!addedGroupByAttributes?.length}
            onClick={() => applyGroupByOptions(GROUP_UPDATE_HANDLERES.CLEAR_ALL)}
          >
            <Clear2Icon />
            <Typography
              variant={addedGroupByAttributes.length ? 'subtitle1' : 'subtitle1LightGrey'}
            >
              {t(
                'bb.settings.productCardConfigSettings.labels.clearGroup',
                'Clear Groups'
              )}
            </Typography>
          </Button>
        </Grid>
      </Grid>
    )
  })
}
